import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Tooltip,
    Typography,
    Tabs, Tab,
    Grid,
    IconButton,
    Button, Paper, Collapse, TextField
} from "@mui/material";
import { tagIcon, calendarIcon, copyIcon, arrowDownIcon, infoIconGrey, crossIcon, warningIcon } from "./icons";
import { dateFormat, deepCopy, } from "../../../app/util";
import RoomPricingTable from './RoomPricingTable';
import PlanNameInput from './PlanNameInput';
import DateRangePickerInput from './DateRangePickerInput';
import EllipsisWithTooltip from './EllipsisWithTooltip';

const SeasonAccordion = forwardRef(function SeasonAccordion({ season, updatePricingSeasonsUI, manageSeasonChangeFunc, isReadOnlyMode, discardCount, addNewPricingRule, scrollTopSeason, inventoryRoomCodes, inventoryRoomSettings }, ref) {
    const seasonEditCopyRef = useRef(deepCopy(season));
    const { ui_state } = useSelector(state => state.app.current_state);
    const pricingSeasonsUI = deepCopy(ui_state.pricingSeasonsUI);
    const { openItemId, changeItemId, isRecalibrating } = pricingSeasonsUI;

    const {
        plan_name,
        rate_plan,
        plan_period,
        last_edited_by,
        last_edited_on,
        isDefault,
        id
    } = seasonEditCopyRef.current;
    const [isRoomTypeDifference, setIsRoomTypeDifference] = useState(season.isRoomTypeDifference || false);
    const [isBaseRoomTypeChanged, setIsBaseRoomTypeChanged] = useState(season.isBaseRoomTypeChanged || false);
    const isNew = (id + "").includes('new_');
    const baseRoomType = season?.base_room_type;
    const isBaseRoomTypeBlank = ['threshold_min', 'threshold_max'].some(key =>
        Object.values(baseRoomType[key] || {}).some(
            val => val === '' || val === null || val === undefined
        )
    );

    const [expanded, setExpanded] = useState(openItemId === id);

    const handleToggle = (id, changeItemId) => {
        if (changeItemId === null) {
            if (openItemId === id) {
                pricingSeasonsUI.openItemId = null;
            } else {
                pricingSeasonsUI.openItemId = id
                if (isRecalibrating) {
                    pricingSeasonsUI.showRecalibratingPopup = true; // open recalibrating popup
                }
            }
            updatePricingSeasonsUI(pricingSeasonsUI);
        }
        else {
            scrollTopSeason(); // Scroll top the current editing season
        }
    }

    const handleCopy = () => {
        if (changeItemId === null) {
            addNewPricingRule('copyNewSeason', id);
        }
        else {
            scrollTopSeason(); // Scroll top the current editing season
        }
    }

    useEffect(() => {
        if (openItemId === id) {
            setExpanded(true);
        } else {
            setExpanded(false);
        }
    }, [openItemId]);

    useEffect(() => {
        seasonEditCopyRef.current = deepCopy(season);
        setIsRoomTypeDifference(season.isRoomTypeDifference);
        setIsBaseRoomTypeChanged(season.isBaseRoomTypeChanged);
    }, [discardCount, JSON.stringify(season)]);

    return (
        <Paper
            ref={ref}
            className={`season-accordion ${expanded ? "expanded" : ""}`}
            elevation={0}
            style={{ position: "relative" }}>
            {season && season.status === 'expired' && <div className="status-tag">was {season?.statusHistory}</div>}
            <Box className="season-accordion-summary">
                <Grid container className="summary-container">
                    {/* Name */}
                    <Grid className="name-section">
                        <Box sx={{ height: "100%", display: "flex", alignItems: "center", gap: "12px" }}>
                            {/* test {isRoomTypeDifference || isBaseRoomTypeBlank && <div>{warningIcon}</div>} */}
                            {(isRoomTypeDifference || isBaseRoomTypeChanged) && season.status !== 'expired' ? (
                                <div>{warningIcon}</div>
                            ) : null}
                            <PlanNameInput
                                season={season}
                                seasonEditCopyRef={seasonEditCopyRef}
                                manageSeasonChangeFunc={manageSeasonChangeFunc}
                                isReadOnlyMode={isReadOnlyMode}
                                expanded={expanded}
                                discardCount={discardCount}
                            />
                        </Box>
                    </Grid>

                    {/* Rate Plan */}
                    <Grid className="rate-plan-section">
                        <Box display="flex" alignItems="center" gap={1.5}>
                            {tagIcon}
                            <Box>
                                <Box className="summary-label">Rate Plan</Box>
                                <Box className="summary-value">{rate_plan}</Box>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Date Range */}

                    <DateRangePickerInput
                        season={season}
                        seasonEditCopyRef={seasonEditCopyRef}
                        manageSeasonChangeFunc={manageSeasonChangeFunc}
                        isReadOnlyMode={isReadOnlyMode}
                        expanded={expanded}
                    />


                    {/* Last Edited */}
                    <Grid className="last-edited-section">
                        <Box className="summary-label">Last Edited By</Box>
                        {isNew ?
                            <Box className="summary-value"> - </Box>
                            :
                            <Box className="summary-value"><EllipsisWithTooltip text={last_edited_by} width='112px' /> | {dateFormat(last_edited_on, "ddd, MMM DD, YYYY")}</Box>
                        }
                    </Grid>

                    {/* Actions */}
                    <Grid className="actions-section">
                        {isRecalibrating ?
                            null
                            :
                            <Tooltip arrow title="Copy">
                                <IconButton disabled={pricingSeasonsUI.changeItemId === id} size="small" color="default" onClick={() => handleCopy(id)}>{copyIcon}</IconButton>
                            </Tooltip>
                        }
                        <IconButton
                            size="small"
                            color="default"
                            className={`accordion-arrow-icon ${expanded ? "expanded" : ""}`}
                            onClick={() => handleToggle(id, changeItemId)}
                        >
                            {arrowDownIcon}
                        </IconButton>
                    </Grid>
                </Grid>
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit className="season-accordion-details">
                <RoomPricingTable
                    seasonEditCopyRef={seasonEditCopyRef}
                    season={season}
                    manageSeasonChangeFunc={manageSeasonChangeFunc}
                    isReadOnlyMode={isReadOnlyMode}
                    isNew={isNew}
                    inventoryRoomCodes={inventoryRoomCodes}
                    inventoryRoomSettings={inventoryRoomSettings}
                />
            </Collapse>
        </Paper>
    );
});

export default SeasonAccordion